import { useDebounce } from '@uidotdev/usehooks';
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Input } from '@/components/ui/catalyst/input';
import { Z_INDEX_DROPDOWN_DEEPLY_NESTED } from '@/components/ui/zIndex';
import { PortalProvider } from '@/services/api/generated/portalProvidersService';
import { useTreatmentPortalsProvidersListQuery } from '@/services/api/portalProviderService';

interface PortalProviderSearchProps {
  value: string;
  onChange: (providerId: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** When rendered inside a modal, use higher z-index for dropdown */
  inModal?: boolean;
}

export const PortalProviderSearch = ({
  value,
  onChange,
  placeholder = 'Type at least 2 characters to search...',
  className,
  disabled = false,
  inModal = false,
}: PortalProviderSearchProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedQuery = useDebounce(searchQuery, 500);

  const { data: portalProviders, isLoading } =
    useTreatmentPortalsProvidersListQuery(
      { query: debouncedQuery },
      { skip: debouncedQuery.length < 2 || disabled },
    );

  // Find selected provider
  const selectedProvider = useMemo(() => {
    if (!value || !portalProviders) return null;
    return portalProviders.find((p) => p.id === value);
  }, [value, portalProviders]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || !portalProviders?.length) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < portalProviders.length - 1 ? prev + 1 : 0,
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : portalProviders.length - 1,
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (portalProviders[highlightedIndex]) {
          handleSelectProvider(portalProviders[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleSelectProvider = (provider: PortalProvider) => {
    onChange(provider.id);
    setSearchQuery(provider.name);
    setIsOpen(false);
    setHighlightedIndex(0);
  };

  // Update search query when selected provider changes
  useEffect(() => {
    if (selectedProvider && !isOpen) {
      setSearchQuery(selectedProvider.name);
    } else if (!value) {
      setSearchQuery('');
    }
  }, [selectedProvider, value, isOpen]);

  return (
    <div className={clsx('relative', className)} ref={dropdownRef}>
      <Input
        ref={inputRef}
        type="text"
        value={searchQuery}
        onChange={(e) => {
          setSearchQuery(e.target.value);
          setIsOpen(true);
          setHighlightedIndex(0);
        }}
        onFocus={() => {
          if (
            searchQuery.length >= 2 ||
            (portalProviders && portalProviders.length > 0)
          ) {
            setIsOpen(true);
          }
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="w-full text-sm"
        disabled={disabled}
      />

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div
          className="absolute mt-1 w-full rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 dark:bg-zinc-800 dark:ring-white/10"
          style={{ zIndex: inModal ? Z_INDEX_DROPDOWN_DEEPLY_NESTED : 10 }}
        >
          <div className="max-h-60 overflow-auto py-1">
            {(() => {
              if (isLoading) {
                return (
                  <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                    Loading...
                  </div>
                );
              }

              if (searchQuery.length < 2) {
                return (
                  <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                    Type at least 2 characters to search
                  </div>
                );
              }

              if (!portalProviders || portalProviders.length === 0) {
                return (
                  <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                    No providers found for &quot;{searchQuery}&quot;
                  </div>
                );
              }

              return portalProviders.map((provider, index) => (
                <button
                  key={provider.id}
                  type="button"
                  onClick={() => handleSelectProvider(provider)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={clsx(
                    'w-full px-4 py-2 text-left',
                    'hover:bg-gray-100 dark:hover:bg-zinc-700',
                    'focus:bg-gray-100 dark:focus:bg-zinc-700 focus:outline-none',
                    highlightedIndex === index &&
                      'bg-gray-100 dark:bg-zinc-700',
                    value === provider.id && 'font-semibold',
                  )}
                >
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {provider.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {provider.portal} • {provider.site || 'No site'}
                  </div>
                </button>
              ));
            })()}
          </div>
        </div>
      )}
    </div>
  );
};
